/**
 * Google Analytics (GA4) loader — consent-gated.
 *
 * Design rules (tied to CoParent Console's privacy-first posture):
 *   • The GA script is NEVER injected until the visitor has actively
 *     granted analytics consent through the cookie banner. No
 *     "pre-consent" tracking, no implied consent on navigation.
 *   • GA is only fired on PUBLIC (non-`/app/*`) pages. Authenticated
 *     parent traffic is never sent to Google.
 *   • `anonymize_ip: true` is set on every `config` call.
 *   • Consent can be withdrawn — `disableGA()` flips the Google
 *     opt-out window flag, and a page reload clears any injected
 *     script state.
 *
 * Consent storage shape in localStorage (`cpc_cookie_consent_v1`):
 *   {
 *     necessary: true,          // always true, not user-toggleable
 *     analytics: true | false,  // GA4
 *     v: 1,                     // schema version for future changes
 *     ts: "2026-02-15T10:00:00Z"
 *   }
 */

const CONSENT_KEY = "cpc_cookie_consent_v1";
const GA_ID = process.env.REACT_APP_GA_ID;
const CONSENT_EVENT = "cpc-consent-change";

export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent() {
  const c = getConsent();
  return !!(c && c.analytics === true);
}

export function setConsent({ analytics }) {
  const payload = {
    necessary: true,
    analytics: !!analytics,
    v: 1,
    ts: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  } catch {
    // localStorage can throw in private-mode Safari; we just drop the
    // persistence silently and the banner will simply show again next
    // visit.
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: payload }));
  return payload;
}

export function clearConsent() {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    // ignore — see setConsent
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }));
}

let _injected = false;

export function initGA() {
  if (!GA_ID) return;
  if (!hasAnalyticsConsent()) return;
  if (_injected) return;
  _injected = true;

  // Clear any previous opt-out so a re-accept after a reject re-enables
  // tracking without requiring a page reload.
  window[`ga-disable-${GA_ID}`] = false;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID, {
    anonymize_ip: true,
    // We fire page_view manually per-route on the SPA so we disable
    // the automatic one to avoid double-counting the first landing.
    send_page_view: false,
  });
}

export function disableGA() {
  if (!GA_ID) return;
  // Google's documented opt-out flag — any subsequent gtag calls
  // are no-ops until this is flipped back to false.
  window[`ga-disable-${GA_ID}`] = true;
}

export function trackPageView(path, title) {
  if (!GA_ID) return;
  if (!hasAnalyticsConsent()) return;
  if (typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.origin + path,
  });
}

/**
 * Fire a custom GA4 event. Silently no-ops if GA isn't loaded or the
 * visitor hasn't granted analytics consent — callers don't need to
 * check; just call at the point of user intent.
 *
 * Conventions for params:
 *   • Use snake_case names (GA4 standard).
 *   • Don't send any PII (email, name, child IDs, household IDs).
 *   • `location` describes where on the site the event fired (e.g.
 *     "landing_hero", "pricing_card") so we can A/B different
 *     placements later without renaming the event.
 */
export function trackEvent(name, params = {}) {
  if (!GA_ID) return;
  if (!hasAnalyticsConsent()) return;
  if (typeof window.gtag !== "function") return;
  window.gtag("event", name, params);
}

export function onConsentChange(listener) {
  const handler = (evt) => listener(evt.detail);
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
}

/**
 * Returns true for routes that should be analytics-tracked.
 * Authenticated parent routes (everything under `/app/*`) are
 * excluded from tracking entirely, in line with the user's "public
 * pages only" choice.
 */
export function isPublicPath(pathname) {
  if (!pathname) return true;
  return !pathname.startsWith("/app");
}
